import React, { useState, useCallback, useEffect } from 'react';
import classNames from 'classnames';
import { emailIsValid } from '../../../../common/config/utils';
import classes from '../classes.module.scss';
import { SvgIcon } from '../../../../common/sprites';
import Spiner from '../../../../common/helpers/spiner';
import withPGTranslation from '../../../config/withPGTranslation';
import GDPRConsernPopup from '../gdpr_consern_popup';
import { GDPRConsernPopupThemes } from '../../../config/themes';
import { analyticsService } from '../../../../common/analytics';
import { ANALYTICS_EVENTS } from '../../../../common/analytics/AnalyticsEvents';

const { shared } = window;

interface BackupEmailScreenProps {
  phoneNumber: string;
  confirmationCode: string;
  consentedToGDPR: boolean;
  consentToMarketingSms: boolean;
  onEmailCodeSent?: () => void;
  onSkip?: () => void;
  t: (key: string) => string;
  clearError?: () => void;
  loading?: boolean;
  registerByPhone: (phoneNumber: string, confirmationCode: string, consentedToGDPR: boolean, consentToMarketingSms: boolean, email: string, signUpFlow?: string) => void;
  shouldShowGDPRConsent: (display: { [key: string]: boolean }) => void;
}

/**
 * BackupEmailScreen component - Screen for adding backup email after phone verification
 * Displays form to add backup email and send verification code to email
 */
function BackupEmailScreen({
  phoneNumber,
  t,
  clearError,
  confirmationCode,
  consentedToGDPR,
  consentToMarketingSms,
  loading: parentLoading = false,
  registerByPhone,
  shouldShowGDPRConsent
}: BackupEmailScreenProps) {
  const [email, setEmail] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [isRegistering, setIsRegistering] = useState(false);

  // Handle email input change
  const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setEmail(value);
    setValidationError(null);
    setApiError(null);
    if (clearError) {
      clearError();
    }
  }, [clearError]);

  // Validate email format
  const validateEmail = useCallback((value: string): string | null => {
    if (!value || value.trim() === '') {
      return t('smsVerification.backup_email_required');
    }
    if (!emailIsValid(value)) {
      return t('smsVerification.backup_email_invalid');
    }
    return null;
  }, [t]);

  // Handle form submit - show GDPR modal first
  const handleSubmit = useCallback(async (e?: React.FormEvent) => {
    e?.preventDefault();

    if (parentLoading) return;

    // Validate email
    const validationErrorResult = validateEmail(email);
    if (validationErrorResult) {
      setValidationError(validationErrorResult);
      return;
    }

    // Track analytics event
    analyticsService.trackEvent(ANALYTICS_EVENTS.PHONE_SIGNUP_ADD_EMAIL_CLICKED);

    // Save email for later use and show GDPR modal
    setPendingEmail(email);
    shouldShowGDPRConsent({ [GDPRConsernPopupThemes.regular]: true });
  }, [email, phoneNumber, confirmationCode, parentLoading, validateEmail, shouldShowGDPRConsent]);

  // Handle register after GDPR consent
  const onRegisterClicked = useCallback(async () => {
    if (pendingEmail) {
      setIsRegistering(true);
      setApiError(null);
      try {
        await registerByPhone(phoneNumber, confirmationCode, consentedToGDPR, consentToMarketingSms, pendingEmail, '');
        setPendingEmail(null);
        setApiError(null);
      } catch (err: any) {
        setPendingEmail(null);
        // Check if it's 409 error (email already exists)
        if (err && err.status === 409) {
          // Extract error message from email field or fullMessages
          const errorMessage =  err.fullMessages?.[0] || 'Email already exists';
          setApiError(errorMessage);
        } else {
          // For other errors - they are already handled in action
          setApiError(null);
        }
      } finally {
        setIsRegistering(false);
      }
    }
  }, [phoneNumber, confirmationCode, pendingEmail, registerByPhone]);

  // Clear validation error when email changes
  useEffect(() => {
    if (validationError && email && validateEmail(email) === null) {
      setValidationError(null);
    }
  }, [email, validationError, validateEmail]);

  const displayError = validationError || apiError;

  const isButtonDisabled = parentLoading || !email || !!validationError || !!apiError || isRegistering;
  const isLoading = isRegistering || parentLoading;

  return (
    <div className={ classes.backupEmailScreenContainer }>
      <div className={ classes.phoneLoginIconContainer }>
        <div className={ classes.phoneLoginIconCircle }>
          <SvgIcon id="verification_phone_icon" />
        </div>
      </div>
      <div className={ classes.backupEmailScreenContent }>

        {/* Title */}
        <div className={ classes.backupEmailScreenTitleContainer }>
          <h2 className={ classes.backupEmailScreenTitle }>
            {t('smsVerification.phone_verified')}
          </h2>
        </div>
        {isRegistering && (
        <div>
          <Spiner
            loaderColor={ shared.appColor }
            loaderSize="44px"
            loaderBorder="4px"
            loaderContainerSize="44px"
            className={ classes.phoneVerificationLoadingSpinner }
          />
        </div>
        )}

        {/* Backup Email Card */}
        {!isRegistering && (
        <div className={ classes.backupEmailCardContainer }>
          <p className={ classes.backupEmailCardText }>
            {t('smsVerification.backup_email_explanation')}
          </p>

          <form onSubmit={ handleSubmit } className={ classes.backupEmailCardForm }>
            <div className={ classes.backupEmailCardInputWrapper }>
              {/* Loading Spinner - positioned absolutely in center */}
              <input
                type="email"
                value={ email }
                onChange={ handleEmailChange }
                placeholder={ t('smsVerification.backup_email_address_placeholder') }
                className={ classNames(
                  classes.backupEmailCardInput,
                  displayError ? classes.backupEmailCardInputError : null
                ) }
                disabled={ isLoading }
              />
              { displayError && (
              <div id="backup-email-error" className={ classes.backupEmailCardError } role="alert">
                { displayError }
              </div>
              ) }
            </div>
            <button
              type="submit"
              className={ classNames(
                classes.backupEmailCardButton,
                isButtonDisabled ? classes.backupEmailCardButtonDisabled : null
              ) }
              disabled={ isButtonDisabled }
            >
              {t('smsVerification.backup_email_submit_button')}
            </button>
          </form>
        </div>
        )}
      </div>
      <GDPRConsernPopup onRegisterClicked={ onRegisterClicked } theme={ GDPRConsernPopupThemes.regular } />
    </div>
  );
}

export default withPGTranslation(BackupEmailScreen);
